import type { ReactNode } from "react"

import type { DashboardDrilldownInsightsOutput } from "../types"

type DashboardDrilldownEvidenceLink =
  DashboardDrilldownInsightsOutput["insights"][number]["evidenceLinks"][number]

interface DashboardInsightEvidenceTextProps {
  text: string
  evidenceLinks: DashboardDrilldownEvidenceLink[]
  onEvidenceLinkClick: (rowRef: string) => void
  onEvidenceLinkHover?: (rowRef: string | null) => void
}

function isMentionBoundary(character: string | undefined): boolean {
  return !character || !/[A-Za-z0-9_-]/.test(character)
}

function getEvidenceMentionCandidates(
  evidenceLinks: DashboardDrilldownEvidenceLink[],
) {
  const rowRefsByLabel = new Map<string, Set<string>>()
  for (const link of evidenceLinks) {
    const label = link.label?.trim()
    if (!label) {
      continue
    }

    const rowRefs = rowRefsByLabel.get(label) ?? new Set<string>()
    rowRefs.add(link.rowRef)
    rowRefsByLabel.set(label, rowRefs)
  }

  const candidates = new Map<
    string,
    { link: DashboardDrilldownEvidenceLink; mentionLabel: string } | null
  >()
  const addCandidate = (input: {
    candidate: string
    link: DashboardDrilldownEvidenceLink
    mentionLabel: string
  }) => {
    const existing = candidates.get(input.candidate)
    if (existing === undefined) {
      candidates.set(input.candidate, {
        link: input.link,
        mentionLabel: input.mentionLabel,
      })
      return
    }

    if (existing !== null && existing.link.rowRef !== input.link.rowRef) {
      candidates.set(input.candidate, null)
    }
  }

  for (const link of evidenceLinks) {
    const label = link.label?.trim()
    const isUniqueLabel = label ? rowRefsByLabel.get(label)?.size === 1 : false

    if (label && isUniqueLabel) {
      addCandidate({ candidate: label, link, mentionLabel: label })
    }

    const rowRef = link.rowRef.trim()
    if (rowRef) {
      addCandidate({
        candidate: rowRef,
        link,
        mentionLabel: isUniqueLabel && label ? label : rowRef,
      })
    }
  }

  return Array.from(candidates.entries())
    .flatMap(([candidate, value]) => (value ? [{ candidate, ...value }] : []))
    .sort((a, b) => b.candidate.length - a.candidate.length)
}

function findEvidenceMentionAt(
  text: string,
  index: number,
  candidates: ReturnType<typeof getEvidenceMentionCandidates>,
): {
  length: number
  link: DashboardDrilldownEvidenceLink
  mentionLabel: string
} | null {
  for (const { candidate, link, mentionLabel } of candidates) {
    const backtickedCandidate = `\`${candidate}\``
    if (text.startsWith(backtickedCandidate, index)) {
      return {
        length: backtickedCandidate.length,
        link,
        mentionLabel,
      }
    }

    if (
      text.startsWith(candidate, index) &&
      isMentionBoundary(text[index - 1]) &&
      isMentionBoundary(text[index + candidate.length])
    ) {
      return { length: candidate.length, link, mentionLabel }
    }
  }

  return null
}

function renderEvidenceText(
  input: DashboardInsightEvidenceTextProps,
): ReactNode[] {
  const candidates = getEvidenceMentionCandidates(input.evidenceLinks)
  const content: ReactNode[] = []
  let textStart = 0
  let index = 0
  let mentionIndex = 0

  while (index < input.text.length) {
    const match = findEvidenceMentionAt(input.text, index, candidates)
    if (!match) {
      index += 1
      continue
    }

    if (textStart < index) {
      content.push(input.text.slice(textStart, index))
    }

    content.push(
      <button
        key={`${match.link.rowRef}-${mentionIndex}`}
        type="button"
        aria-label={`View evidence for row ${match.mentionLabel}`}
        className="inline-flex h-5 items-center rounded-sm border border-coval-border-active bg-surface-0 px-1.5 font-mono text-xs font-medium leading-none text-coval-text align-baseline transition-colors hover:border-coval-border-active hover:bg-surface-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-coval-contrast"
        onClick={() => input.onEvidenceLinkClick(match.link.rowRef)}
        onMouseEnter={() => input.onEvidenceLinkHover?.(match.link.rowRef)}
        onMouseLeave={() => input.onEvidenceLinkHover?.(null)}
        onFocus={() => input.onEvidenceLinkHover?.(match.link.rowRef)}
        onBlur={() => input.onEvidenceLinkHover?.(null)}
        data-testid={`dashboard-insight-evidence-${match.link.rowRef}`}
      >
        {match.mentionLabel}
      </button>,
    )

    mentionIndex += 1
    index += match.length
    textStart = index
  }

  if (textStart < input.text.length) {
    content.push(input.text.slice(textStart))
  }

  return content
}

export function DashboardInsightEvidenceText(
  props: DashboardInsightEvidenceTextProps,
) {
  return <>{renderEvidenceText(props)}</>
}
